import React, { useState, useEffect, useCallback } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Search, Package, ArrowRight, ChevronLeft, ChevronRight, X, FileText, Truck, Factory, Download } from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';

const TYPE_COLORS = {
  purchase_receipt: 'bg-blue-50 text-blue-700 border-blue-200',
  challan_line: 'bg-amber-50 text-amber-700 border-amber-200',
  customer_wo: 'bg-purple-50 text-purple-700 border-purple-200',
  auto: 'bg-slate-50 text-slate-600 border-slate-200',
};

const STATUS_COLORS = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partially_dispatched: 'bg-orange-50 text-orange-700 border-orange-200',
  fully_dispatched: 'bg-slate-100 text-slate-500 border-slate-200',
  exhausted: 'bg-red-50 text-red-600 border-red-200',
};

const EVENT_ICONS = {
  receipt: FileText,
  production: Factory,
  dispatch_manifest: Package,
  delivery_challan: Truck,
};

export default function TraceabilityPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [traceType, setTraceType] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);

  const doSearch = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 30 };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (traceType) params.trace_type = traceType;
      if (status) params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await inventoryAPI.traceSearch(params);
      setResults(res.data.trace_lots || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 0);
      setPage(p);
    } catch (err) {
      showApiError(err, 'Failed to search traces');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, traceType, status, dateFrom, dateTo]);

  useEffect(() => { doSearch(1); }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    doSearch(1);
  };

  const openLifecycle = async (lot) => {
    setSelectedLot(lot);
    setLifecycleLoading(true);
    try {
      const res = await inventoryAPI.traceLifecycleDetail(lot.id);
      setLifecycle(res.data);
    } catch (err) {
      showApiError(err, 'Failed to load lifecycle');
    } finally {
      setLifecycleLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTraceType('');
    setStatus('');
    setDateFrom('');
    setDateTo('');
    setTimeout(() => doSearch(1), 50);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div className="space-y-4" data-testid="traceability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Traceability Search</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Search across all trace lots by ID, invoice, lot, heat code, customer WO, or item</p>
        </div>
        <Badge variant="outline" className="text-xs">{total} trace{total !== 1 ? 's' : ''}</Badge>
      </div>

      {/* Search Bar */}
      <Card data-testid="trace-search-card">
        <CardContent className="p-3">
          <form onSubmit={handleSearch} className="space-y-2">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by trace ID, invoice #, lot #, heat code, customer WO, item name, supplier..."
                  className="h-8 text-xs pl-8"
                  data-testid="trace-search-input"
                />
              </div>
              <Button type="submit" size="sm" className="h-8 px-4 text-xs" disabled={loading} data-testid="trace-search-btn">
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap items-end">
              <div>
                <Label className="text-[10px] text-muted-foreground">Type</Label>
                <Select value={traceType} onValueChange={setTraceType}>
                  <SelectTrigger className="h-7 text-xs w-[140px]" data-testid="trace-type-filter">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="purchase_receipt">Purchase Receipt</SelectItem>
                    <SelectItem value="challan_line">Challan Line</SelectItem>
                    <SelectItem value="customer_wo">Customer WO</SelectItem>
                    <SelectItem value="auto">Auto-generated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-7 text-xs w-[150px]" data-testid="trace-status-filter">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="partially_dispatched">Partially Dispatched</SelectItem>
                    <SelectItem value="fully_dispatched">Fully Dispatched</SelectItem>
                    <SelectItem value="exhausted">Exhausted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">From</Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs w-[130px]" data-testid="trace-date-from" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">To</Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs w-[130px]" data-testid="trace-date-to" />
              </div>
              <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={clearFilters} data-testid="trace-clear-filters">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card data-testid="trace-results-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-xs text-muted-foreground">Searching...</div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-xs text-muted-foreground">
              No trace lots found. Try adjusting your search criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="trace-results-table">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="p-2 text-left font-medium">Trace ID</th>
                    <th className="p-2 text-left font-medium">Type</th>
                    <th className="p-2 text-left font-medium">Item</th>
                    <th className="p-2 text-left font-medium">Godown</th>
                    <th className="p-2 text-right font-medium">Qty</th>
                    <th className="p-2 text-right font-medium">Available</th>
                    <th className="p-2 text-left font-medium">Source</th>
                    <th className="p-2 text-left font-medium">Status</th>
                    <th className="p-2 text-left font-medium">Date</th>
                    <th className="p-2 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {results.map(lot => (
                    <tr key={lot.id} className="hover:bg-muted/30 transition-colors" data-testid={`trace-row-${lot.id}`}>
                      <td className="p-2 font-mono text-[11px] max-w-[200px]">
                        <span className="truncate block" title={lot.trace_id}>{lot.trace_id}</span>
                        {lot.trace_label && lot.trace_label !== lot.trace_id && (
                          <span className="text-[9px] text-muted-foreground block mt-0.5 truncate">{lot.trace_label}</span>
                        )}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className={`text-[9px] ${TYPE_COLORS[lot.trace_type] || ''}`}>
                          {lot.trace_type === 'purchase_receipt' ? 'Purchase' : lot.trace_type === 'challan_line' ? 'Challan' : lot.trace_type === 'customer_wo' ? 'Cust WO' : 'Auto'}
                        </Badge>
                      </td>
                      <td className="p-2 max-w-[150px]">
                        <span className="truncate block">{lot.item_name}</span>
                        {lot.item_unique_id && <span className="text-[9px] text-muted-foreground">{lot.item_unique_id}</span>}
                      </td>
                      <td className="p-2 text-muted-foreground">{lot.godown_name}</td>
                      <td className="p-2 text-right font-mono">{lot.quantity}</td>
                      <td className="p-2 text-right font-mono font-medium">{lot.available_quantity}</td>
                      <td className="p-2 max-w-[140px]">
                        {lot.wo_number && <span className="block text-[10px]">WO: {lot.wo_number}</span>}
                        {lot.challan_number && <span className="block text-[10px]">DC: {lot.challan_number}</span>}
                        {lot.po_number && <span className="block text-[10px]">PO: {lot.po_number}</span>}
                        {lot.invoice_number && <span className="block text-[10px]">INV: {lot.invoice_number}</span>}
                        {lot.lot_number && <span className="block text-[10px]">LOT: {lot.lot_number}</span>}
                        {lot.heat_code && <span className="block text-[10px]">HC: {lot.heat_code}</span>}
                        {lot.coc_number && <span className="block text-[10px]">CoC: {lot.coc_number}</span>}
                        {lot.customer_name && <span className="block text-[10px] text-muted-foreground">{lot.customer_name}</span>}
                        {lot.supplier_name && <span className="block text-[10px] text-muted-foreground">Sup: {lot.supplier_name}</span>}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className={`text-[9px] ${STATUS_COLORS[lot.status] || ''}`}>
                          {(lot.status || 'available').replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-2 text-muted-foreground text-[10px]">{formatDate(lot.created_at)}</td>
                      <td className="p-2">
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => openLifecycle(lot)} data-testid={`trace-lifecycle-${lot.id}`}>
                          Lifecycle <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between p-2 border-t text-xs">
              <span className="text-muted-foreground">Page {page} of {pages} ({total} results)</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-6 text-xs" disabled={page <= 1} onClick={() => doSearch(page - 1)} data-testid="trace-prev-page">
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <Button variant="outline" size="sm" className="h-6 text-xs" disabled={page >= pages} onClick={() => doSearch(page + 1)} data-testid="trace-next-page">
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lifecycle Dialog */}
      <Dialog open={!!selectedLot} onOpenChange={() => { setSelectedLot(null); setLifecycle(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Search className="w-4 h-4 text-orange-600" />
              Trace Lifecycle
            </DialogTitle>
          </DialogHeader>
          {selectedLot && (
            <div className="space-y-4" data-testid="trace-lifecycle-dialog">
              {/* Trace Summary */}
              <div className="p-3 rounded-md bg-muted/50 text-xs space-y-1">
                <p><span className="text-muted-foreground">Trace ID:</span> <strong className="font-mono">{selectedLot.trace_id}</strong></p>
                <p><span className="text-muted-foreground">Item:</span> <strong>{selectedLot.item_name}</strong> {selectedLot.item_unique_id && <span className="text-muted-foreground">({selectedLot.item_unique_id})</span>}</p>
                <div className="flex gap-4 mt-1">
                  <span><span className="text-muted-foreground">Total:</span> {selectedLot.quantity}</span>
                  <span><span className="text-muted-foreground">Available:</span> <strong className="text-emerald-600">{selectedLot.available_quantity}</strong></span>
                  <span><span className="text-muted-foreground">Dispatched:</span> {selectedLot.dispatched_quantity || 0}</span>
                </div>
                <div className="flex gap-4 mt-1 flex-wrap">
                  {selectedLot.invoice_number && <span><span className="text-muted-foreground">Invoice:</span> {selectedLot.invoice_number}</span>}
                  {selectedLot.lot_number && <span><span className="text-muted-foreground">Lot:</span> {selectedLot.lot_number}</span>}
                  {selectedLot.heat_code && <span><span className="text-muted-foreground">Heat Code:</span> {selectedLot.heat_code}</span>}
                  {selectedLot.coc_number && <span><span className="text-muted-foreground">CoC:</span> {selectedLot.coc_number}</span>}
                  {selectedLot.wo_number && <span><span className="text-muted-foreground">WO:</span> {selectedLot.wo_number}</span>}
                  {selectedLot.challan_number && <span><span className="text-muted-foreground">DC:</span> {selectedLot.challan_number}</span>}
                  {selectedLot.po_number && <span><span className="text-muted-foreground">PO:</span> {selectedLot.po_number}</span>}
                  {selectedLot.customer_name && <span><span className="text-muted-foreground">Customer:</span> {selectedLot.customer_name}</span>}
                  {selectedLot.supplier_name && <span><span className="text-muted-foreground">Supplier:</span> {selectedLot.supplier_name}</span>}
                  {selectedLot.customer_wo_number && <span><span className="text-muted-foreground">Cust WO:</span> {selectedLot.customer_wo_number}</span>}
                </div>
              </div>

              {/* Timeline */}
              {lifecycleLoading ? (
                <div className="text-center text-xs text-muted-foreground py-6">Loading lifecycle...</div>
              ) : lifecycle ? (
                <div>
                  <h3 className="text-xs font-medium mb-2">Lifecycle Events ({lifecycle.total_events})</h3>
                  {lifecycle.events.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center">No lifecycle events found.</p>
                  ) : (
                    <div className="space-y-0 relative">
                      {/* Vertical timeline line */}
                      <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />
                      {lifecycle.events.map((event, idx) => {
                        const Icon = EVENT_ICONS[event.type] || FileText;
                        const isLast = idx === lifecycle.events.length - 1;
                        return (
                          <div key={idx} className="flex gap-3 relative pb-3" data-testid={`lifecycle-event-${idx}`}>
                            <div className={`w-[30px] h-[30px] rounded-full flex items-center justify-center z-10 flex-shrink-0 ${
                              event.type === 'receipt' ? 'bg-blue-100 text-blue-600' :
                              event.type === 'production' ? 'bg-amber-100 text-amber-600' :
                              event.type === 'dispatch_manifest' ? 'bg-purple-100 text-purple-600' :
                              event.type === 'delivery_challan' ? 'bg-emerald-100 text-emerald-600' :
                              'bg-slate-100 text-slate-500'
                            }`}>
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium capitalize flex items-center gap-2">
                                <span>{event.type.replace(/_/g, ' ')}</span>
                                {event.status === 'cancelled' && (
                                  <Badge className="text-[9px] bg-red-100 text-red-700 border border-red-200 px-1.5 py-0" data-testid={`lifecycle-cancelled-${idx}`}>Cancelled</Badge>
                                )}
                              </p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">{event.description}</p>
                              <p className="text-[10px] text-muted-foreground/70 mt-0.5">{formatDate(event.date)}</p>
                              {event.created_by_name && <p className="text-[10px] text-muted-foreground/70">By: {event.created_by_name}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
